import type { KyInstance } from 'ky';
import { erc20Abi, getAddress } from 'viem';

import {
    ANCHOR_CHAINS,
    ANCHOR_CHAIN_IDS,
    CRONOS_CHAIN_ID,
    DEFAULT_ANCHOR_CHAIN_ID,
    ROBINHOOD_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import {
    CRONOS_USDC_ADDRESS,
    CRONOS_USDC_NATIVE_ADDRESS,
    fetchBalanceLight,
    fetchSpeedConfig,
    ROBINHOOD_USDG_ADDRESS,
} from '@/lib/api/speed';
import type { BalanceLightResponse } from '@/lib/api/speed';
import { hyperEvmPublicClient } from '@/lib/hyperEvmPublicClient';

import { hasUsableAdapter } from './directWithdraw';

/**
 * Deposits that ORIGINATE on HyperEVM (chain 999), where the user already holds USDC — the
 * mirror image of the EOA-direct withdrawal in `directWithdraw.ts`. Two routes exist and the
 * user never picks between them; the wallet's own state decides:
 *
 *   - `selfFund` — the user already has a per-user adapter deployed, so the funds can go
 *     straight in: `USDC.approve(adapter, amount)` then `HyperliquidManager.selfFund(amount)`,
 *     both signed by the EOA on 999. No backend job, no service wallet, no bridge.
 *   - `saga` — no adapter yet (brand-new user, or `GET /speed/balance/light` refuses with
 *     `custodialWalletMissing`). The backend deposit saga onboards server-side and answers
 *     `route: 'hyperevmDirect'`: a plain ERC20 transfer to the custodial wallet it just
 *     provisioned, recorded against the job and tracked like any other deposit.
 *
 * Everything here is pure/planning; the executors live in `hooks/useDirectDeposit.ts`
 * (selfFund) and `hooks/useCustodialDeposit.ts` (saga).
 */

export const SELF_FUND_ABI = [
    {
        type: 'function',
        name: 'selfFund',
        stateMutability: 'nonpayable',
        inputs: [{ name: 'amount', type: 'uint256' }],
        outputs: [],
    },
] as const;

/** Where the stable being deposited comes from. */
export type DepositSource =
    | AnchorDepositSource
    | 'hyperEvm'
    /**
     * USDC the custodial trading wallet already holds on HyperEVM, moved to HyperCore. Never a
     * picker choice: the user only ever sees their HyperCore balance, so a residue here is
     * surfaced as a one-button notice on the balance pane (HyperCoreTopUpNotice).
     */
    | 'walletBalance';

/**
 * The origins that live on an AUTHORIZATION ANCHOR. Each one bridges to HyperEVM
 * USDC via Relay; which of them the picker offers depends on the anchor the
 * account is on, because that is the chain the user's wallet is asked to sign on.
 */
export type AnchorDepositSource =
    | 'cronosUsdc'
    | 'cronosUsdcE'
    | 'robinhoodUsdg';

export interface AnchorDepositToken {
    address: `0x${string}`;
    symbol: string;
    note: string;
    chainId: AnchorChainId;
}

export const ANCHOR_DEPOSIT_TOKENS: Record<
    AnchorDepositSource,
    AnchorDepositToken
> = {
    // Two different tokens, both called "USDC" in conversation, which is exactly
    // why the picker has to name them. `USDC` is Circle's own issuance on Cronos;
    // `USDC.e` is the bridged one. Holding one is no help if the form is pointed
    // at the other, so the label carries the symbol even when the balance is zero.
    cronosUsdc: {
        address: CRONOS_USDC_NATIVE_ADDRESS,
        symbol: 'USDC',
        note: 'Circle-issued',
        chainId: CRONOS_CHAIN_ID,
    },
    cronosUsdcE: {
        address: CRONOS_USDC_ADDRESS,
        symbol: 'USDC.e',
        note: 'bridged',
        chainId: CRONOS_CHAIN_ID,
    },
    robinhoodUsdg: {
        address: ROBINHOOD_USDG_ADDRESS,
        symbol: 'USDG',
        note: 'native stable',
        chainId: ROBINHOOD_CHAIN_ID,
    },
};

/** The anchor origins offered on each anchor, in picker order. */
export const ANCHOR_DEPOSIT_SOURCES: Record<
    AnchorChainId,
    readonly AnchorDepositSource[]
> = {
    [CRONOS_CHAIN_ID]: ['cronosUsdc', 'cronosUsdcE'],
    [ROBINHOOD_CHAIN_ID]: ['robinhoodUsdg'],
};

export function isAnchorSource(
    source: DepositSource,
): source is AnchorDepositSource {
    return source in ANCHOR_DEPOSIT_TOKENS;
}

/**
 * Every anchor origin across BOTH chains, the preferred anchor's tokens first.
 * The deposit picker offers any funded stable wherever it lives; the preferred
 * anchor (the chain the wallet sits on) only decides listing order and the
 * empty-wallet default.
 */
export function allAnchorSources(
    preferredAnchor: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): AnchorDepositSource[] {
    const rest = ANCHOR_CHAIN_IDS.filter(
        (chainId) => chainId !== preferredAnchor,
    );
    return [preferredAnchor, ...rest].flatMap(
        (chainId) => [...ANCHOR_DEPOSIT_SOURCES[chainId]],
    );
}

/** The origin an anchor falls back to when the user holds nothing anywhere. */
export function defaultAnchorSource(
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): AnchorDepositSource {
    const sources = ANCHOR_DEPOSIT_SOURCES[anchorChainId];
    // the established route on Cronos is the bridged USDC.e (the last entry);
    // a single-token anchor has only one answer.
    return sources[sources.length - 1]!;
}

/** Per-token EOA balances on the anchor (display units); null while in flight. */
export type AnchorBalances = Partial<
    Record<AnchorDepositSource, number | null>
>;

export function anchorBalanceFor(
    balances: AnchorBalances,
    source: AnchorDepositSource,
): number | null {
    return balances[source] ?? null;
}

/** Which HyperEVM deposit route applies — see the module comment. */
export type HyperDepositRoute = 'selfFund' | 'saga';

/**
 * HyperCore charges a one-time $1 fee on an account's FIRST transfer out of its trading
 * account (the sender pays it, whatever the destination). It is held back as reserve rather
 * than taken from a withdrawal, so a first funding smaller than this would leave the account
 * with nothing it can actually move. The form refuses it rather than silently stranding the
 * user's deposit. (The constant name is historical — this was first read as an activation
 * charge on the first funding.)
 */
export const HYPER_CORE_ACTIVATION_FEE_USD = 1;
export const HYPER_FIRST_FUNDING_MIN_6 = 2_000_000n; // $2

/**
 * `selfFund` needs an adapter to fund. Anything else — no adapter, or a balance/light that
 * refused because the user has no custodial wallet at all — goes through the backend saga,
 * which onboards the user server-side before asking for the transfer.
 */
export function chooseHyperDepositRoute(
    light: Pick<BalanceLightResponse, 'adapter'> | null,
): HyperDepositRoute {
    return light && hasUsableAdapter(light) ? 'selfFund' : 'saga';
}

export interface DepositSourceInput {
    /** Preferred anchor: its tokens list first and win the empty default. */
    anchorChainId?: AnchorChainId;
    /** EOA stable balances on the anchor (display units); null while in flight. */
    anchorBalances: AnchorBalances;
    /** EOA USDC on HyperEVM (display units); null while the read is in flight. */
    hyperBalance: number | null;
    /** null while the HYPE gas read is in flight. */
    hypeGasOk: boolean | null;
}

/** One row of the deposit "From" picker. */
export interface DepositSourceOption {
    source: DepositSource;
    /** what the user reads: symbol, route, and the balance they hold. */
    label: string;
    /** false when the route cannot complete — listed so the user can see what to fund. */
    enabled: boolean;
}

export interface DepositSourcePicker {
    /** The picker's rows, in display order. */
    rows: DepositSourceOption[];
    /** What to select while the user has not touched the picker. */
    defaultSource: DepositSource;
}

/** `$12.34`, or a reason when there is no number to show. */
function balanceText(balance: number | null): string {
    return balance === null ? 'checking…' : `$${balance.toFixed(2)}`;
}

/**
 * The picker's rows, in display order.
 *
 * Every token of the CURRENT ANCHOR is listed, always, including the ones at
 * zero — that is the point. Cronos carries TWO tokens both spoken of as "USDC"
 * (Circle's own, and the bridged USDC.e), and the old picker hid whichever was
 * empty, collapsing to a single unnamed "Cronos (bridged, no USDC)" row when
 * both were. A user holding the other one could not tell which they were being
 * asked for, and a user holding neither could not tell what to go and buy.
 *
 * Empty rows stay disabled: seeing the token is useful, selecting a route that
 * cannot complete is not.
 *
 * Ordering is funded-first (largest balance first), then the empties, so the row
 * you can actually use is never below one you cannot.
 *
 * The preselect rules, unchanged from when a second function computed them:
 * the largest funded anchor token (the anchor wins over HyperEVM — it is the
 * established route, and switching the wallet's chain is disruptive); else
 * HyperEVM when it is wired, funded AND its gas read confirmed; else the
 * preferred anchor's established token, keeping the empty-wallet messaging.
 */
export function buildDepositSourceOptions(
    input: DepositSourceInput & { hyperAvailable: boolean },
): DepositSourcePicker {
    const anchorChainId = input.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID;
    const anchorRows = allAnchorSources(anchorChainId).map((source) => {
        const balance = anchorBalanceFor(input.anchorBalances, source);
        const token = ANCHOR_DEPOSIT_TOKENS[source];
        const chainLabel = ANCHOR_CHAINS[token.chainId].label;
        return {
            source: source as DepositSource,
            label: `${token.symbol} · ${chainLabel} (${token.note}) · ${balanceText(balance)}`,
            enabled: (balance ?? 0) > 0,
            sortBy: balance ?? 0,
        };
    });

    const rows = [...anchorRows];

    if (input.hyperAvailable) {
        // HyperEVM's USDC needs native HYPE too, because the user signs there
        // themselves — worth saying in the row rather than only after selecting.
        const gasBlocked =
            (input.hyperBalance ?? 0) > 0 && input.hypeGasOk === false;
        const detail = gasBlocked
            ? 'needs HYPE for gas'
            : balanceText(input.hyperBalance);
        rows.push({
            source: 'hyperEvm',
            label: `USDC · HyperEVM (direct) · ${detail}`,
            enabled: (input.hyperBalance ?? 0) > 0 && input.hypeGasOk === true,
            sortBy: input.hyperBalance ?? 0,
        });
    }

    const sorted = rows
        .sort((a, b) => {
            if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
            return b.sortBy - a.sortBy;
        })
        .map(({ source, label, enabled }) => ({ source, label, enabled }));

    // the sort is stable and descending, so the first enabled anchor row IS
    // the largest funded anchor token
    const fundedAnchor = sorted.find(
        (row) => row.enabled && row.source !== 'hyperEvm',
    );
    const hyper = sorted.find(
        (row) => row.source === 'hyperEvm' && row.enabled,
    );
    return {
        rows: sorted,
        defaultSource:
            fundedAnchor?.source ??
            hyper?.source ??
            defaultAnchorSource(anchorChainId),
    };
}

export type DepositAmountBlocker =
    'empty' | 'exceedsBalance' | 'belowActivationMinimum';

/**
 * Amount validation for a HyperEVM-sourced deposit. `requiresActivationMinimum` is the
 * first-funding case, where the $1 first-transfer-out fee has to stay covered (see
 * {@link HYPER_FIRST_FUNDING_MIN_6}).
 */
export function validateHyperDepositAmount(input: {
    amount6: bigint;
    /** EOA USDC on HyperEVM in base units; null while unknown (cannot cap yet). */
    balance6: bigint | null;
    requiresActivationMinimum: boolean;
}): DepositAmountBlocker | null {
    if (input.amount6 <= 0n) {
        return 'empty';
    }
    if (input.balance6 !== null && input.amount6 > input.balance6) {
        return 'exceedsBalance';
    }
    if (
        input.requiresActivationMinimum &&
        input.amount6 < HYPER_FIRST_FUNDING_MIN_6
    ) {
        return 'belowActivationMinimum';
    }
    return null;
}

/**
 * The USDC token to move on HyperEVM. `balance/light` is authoritative (it is the same token
 * the balance panel reads), but it 4xx's for users who have no custodial wallet yet — exactly
 * the users the saga route exists for — so the public config answers for them.
 */
export async function resolveHyperUsdcAddress(
    factionsAuthedApi: KyInstance | null,
    publicApi: KyInstance,
): Promise<string> {
    if (factionsAuthedApi) {
        try {
            return (await fetchBalanceLight(factionsAuthedApi)).usdc;
        } catch (error) {
            console.warn(
                '[DirectDeposit] balance/light unavailable; using /speed/config for the USDC address',
                error,
            );
        }
    }
    return (await fetchSpeedConfig(publicApi)).usdc;
}

/** ERC20 allowance on HyperEVM (6dp) — lets a repeat deposit skip the approval signature. */
export async function readEvmAllowance6(
    usdc: string,
    owner: string,
    spender: string,
): Promise<bigint> {
    return hyperEvmPublicClient.readContract({
        address: getAddress(usdc),
        abi: erc20Abi,
        functionName: 'allowance',
        args: [getAddress(owner), getAddress(spender)],
    });
}
